import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import SignatureCanvas from 'react-signature-canvas';

import Button from '../../componentes/ui/Button';
import { LoginContext } from '../../hooks/context/LoginContext';
import { axiosPost, getApiUrl } from '../../helpers/helperApp';

function Signs({ agentId, agentName }) {
  const sigCanvasRef = useRef(null);

  const { apiKey } = useContext(LoginContext);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nombre, setNombre] = useState('');
  const [cargo, setCargo] = useState('');

  const canSign = useMemo(() => !!agentId && parseInt(agentId, 10) > 0, [agentId]);
  const storageKey = useMemo(() => `agentes_signature_${agentId || '0'}`, [agentId]);

  const dataURLtoFile = useCallback((dataURL, fileName) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], fileName, { type: mime });
  }, []);

  const saveSignatureToStorage = useCallback(
    signatureDataUrl => {
      if (!canSign) return;
      const payload = {
        nombre,
        cargo,
        signatureDataUrl,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    },
    [canSign, cargo, nombre, storageKey],
  );

  const loadSignatureFromStorage = useCallback(() => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setNombre('');
      setCargo('');
      if (sigCanvasRef.current) sigCanvasRef.current.clear();
      return false;
    }

    try {
      const stored = JSON.parse(raw);
      setNombre(stored?.nombre || '');
      setCargo(stored?.cargo || '');

      if (sigCanvasRef.current) {
        sigCanvasRef.current.clear();
        if (stored?.signatureDataUrl) {
          sigCanvasRef.current.fromDataURL(stored.signatureDataUrl);
        }
      }
      return true;
    } catch (e) {
      localStorage.removeItem(storageKey);
      setNombre('');
      setCargo('');
      if (sigCanvasRef.current) sigCanvasRef.current.clear();
      return false;
    }
  }, [storageKey]);

  const loadSignature = useCallback(async () => {
    if (!canSign) {
      setNombre('');
      setCargo('');
      if (sigCanvasRef.current) sigCanvasRef.current.clear();
      return;
    }

    try {
      const data = new FormData();
      data.append('validacion', apiKey());
      data.append('agentId', agentId);

      const endpoint = '/agentes/getSignature';
      const url = `${getApiUrl(window.location.origin)}${endpoint}`;
      const response = await axiosPost(url, data);
      const payload = response?.data;

      if (!payload?.success) {
        loadSignatureFromStorage();
        return;
      }

      const signatureDataUrl = payload.sign ? `data:image/png;base64,${payload.sign}` : '';
      const nextNombre = payload.sign_name || '';
      const nextCargo = payload.sign_position || '';

      setNombre(nextNombre);
      setCargo(nextCargo);
      if (sigCanvasRef.current) {
        sigCanvasRef.current.clear();
        if (signatureDataUrl) sigCanvasRef.current.fromDataURL(signatureDataUrl);
      }

      if (signatureDataUrl || nextNombre || nextCargo) {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            nombre: nextNombre,
            cargo: nextCargo,
            signatureDataUrl,
            updatedAt: new Date().toISOString(),
          }),
        );
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (e) {
      loadSignatureFromStorage();
    }
  }, [agentId, apiKey, canSign, loadSignatureFromStorage, storageKey]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) loadSignature();
    return () => {
      isMounted = false;
    };
  }, [loadSignature]);

  const clearSignature = useCallback(() => {
    setError('');
    setSuccess('');
    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  }, []);

  const saveSignature = useCallback(async () => {
    setError('');
    setSuccess('');

    if (!canSign) {
      setError('Selecciona un agente antes de guardar la firma.');
      return;
    }

    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      setError('La firma está vacía.');
      return;
    }

    setSaving(true);
    try {
      const signatureDataUrl = sigCanvasRef.current.toDataURL('image/png');
      const signatureFile = dataURLtoFile(signatureDataUrl, 'signature.png');

      const data = new FormData();
      data.append('validacion', apiKey());
      data.append('agentId', agentId);
      data.append('firma', signatureFile);
      data.append('nombre', nombre);
      data.append('cargo', cargo);


      const endpoint = '/agentes/saveSignature';
      const url = `${getApiUrl(window.location.origin)}${endpoint}`;

      await axiosPost(url, data);
      saveSignatureToStorage(signatureDataUrl);
      await loadSignature();
      setSuccess('Datos guardados correctamente.');
    } catch (e) {
      setError('No se pudo guardar la firma (endpoint pendiente o error de servidor).');
    } finally {
      setSaving(false);
    }
  }, [agentId, apiKey, canSign, dataURLtoFile, nombre, cargo, saveSignatureToStorage, loadSignature]);

  return (
    <Container fluid className="mt-3">
      <Row className="mb-2">
        <Col>
          <h4 className="mb-0">Firmas{agentName ? ` - ${agentName}` : ''}</h4>
          <small className="text-muted">Dibuja una firma y guárdala para este agente.</small>
        </Col>
      </Row>

      {!canSign && (
        <Alert variant="warning">
          Debes seleccionar/guardar un agente para poder asociar la firma.
        </Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Row>
        <Col xs={12} md={6} lg={5}>
          <div>
            <SignatureCanvas
              id="signs"
              ref={sigCanvasRef}
              penColor="rgb(25,78,146)"
              backgroundColor="rgb(255,255,255)"
              canvasProps={{ width: 500, height: 300, className: 'canvaFirma' }}
            />
          </div>
        </Col>
        <Col xs={12} md={4} lg={3} className="d-flex flex-column gap-3 mt-2 mt-md-0, justifyContent: 'flex-end'">

          <div>
            <label htmlFor="signNombre" className="form-label fw-semibold">
              Nombre
            </label>
            <input
              id="signNombre"
              type="text"
              className="form-control"
              placeholder="Nombre del firmante"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="mt-2">
            <label htmlFor="signCargo" className="form-label fw-semibold">
              Cargo
            </label>
            <input
              id="signCargo"
              type="text"
              className="form-control"
              placeholder="Cargo del firmante"
              value={cargo}
              onChange={e => setCargo(e.target.value)}
              disabled={saving}
            />
          </div>

        </Col>
        <Col xs={12} md={4} lg={3} className="d-flex flex-column mt-2 mt-md-0 justify-content-end align-items-end">

            <div className="mt-2" style={{ display: 'flex', gap: 8}}>
                <Button
                clases="rounded"
                color="warning"
                type="button"
                icono=""
                id="btnLimpiarFirmaAgente"
                texto="Limpiar"
                accion={clearSignature}
                disabled={saving}
                />
                <Button
                clases="rounded"
                color="success"
                type="button"
                icono=""
                id="btnGuardarFirmaAgente"
                texto={saving ? 'Guardando...' : 'Guardar firma'}
                accion={saveSignature}
                disabled={!canSign || saving}
                />
                {saving && <Spinner animation="border" size="sm" style={{ alignSelf: 'center' }} />}
            </div>

        </Col>
      </Row>
    </Container>
  );
}

export default Signs;
